import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./FacilityList.module.css";
import SafeText from "./SafeText";
import GoogleMapButton from "./GoogleMapButton";
import BookmarkButton from "./BookmarkButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhoneSquare,
  faTableList,
  faCaretSquareDown
} from "@fortawesome/free-solid-svg-icons";
import CurrencyFormatter from "./CurrencyFormatter";
import { CSSTransition } from "react-transition-group";
import fade from "./fade.module.scss";

const FacilityList = ({
  filteredFacilities,
  handleBookmark,
  bookmarkedFacilities,
}) => {
  const [openIds, setOpenIds] = useState([]);
  const [displayFacilities, setDisplayFacilities] = useState([]);
  const [loadedCount, setLoadedCount] = useState(0);
  const itemsPerPage = 20;
  const observer = useRef();

  const loadMoreItems = useCallback(() => {
    const nextItems = filteredFacilities.slice(
      loadedCount,
      loadedCount + itemsPerPage
    );
    setDisplayFacilities((prevItems) => [...prevItems, ...nextItems]);
    setLoadedCount((prevCount) => prevCount + itemsPerPage);
  }, [filteredFacilities, loadedCount]);

  const lastItemRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          loadedCount < filteredFacilities.length
        ) {
          loadMoreItems();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loadMoreItems, loadedCount, filteredFacilities.length]
  );

  // filteredFacilitiesが更新されたら、loadedCountを0にリセットし、
  // displayFacilitiesを空にする
  useEffect(() => {
    setLoadedCount(0);
    setDisplayFacilities([]);
  }, [filteredFacilities]);

  const handleFacilityClick = (facility) => {
    const id = facility.id;
    if (facility.toshinkyo_tokutei !== "") {
      return;
    }
    setOpenIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  return (
    <div className={styles.container}>
      {displayFacilities.map((facility) => (
        <div className={styles.wrapper} key={facility.id}>
          <div className={styles.course}>
            {facility.A !== "" ? (
              <div className={`${styles.badge01} ${styles.badge}`}>
                <span>生活習慣病A</span>
              </div>
            ) : (
              ""
            )}
            {facility.B !== "" ? (
              <div className={`${styles.badge02} ${styles.badge}`}>
                <span>生活習慣病B</span>
              </div>
            ) : (
              ""
            )}
            {facility.C !== "" ? (
              <div className={`${styles.badge03} ${styles.badge}`}>
                <span>女性生活習慣病C</span>
              </div>
            ) : (
              ""
            )}
            {facility.B1 !== "" ? (
              <div className={`${styles.badge04} ${styles.badge}`}>
                <span>生活習慣病B1</span>
              </div>
            ) : (
              ""
            )}
            {facility.D !== "" ? (
              <div className={`${styles.badge05} ${styles.badge}`}>
                <span>人間ドック</span>
              </div>
            ) : (
              ""
            )}
            {facility.L !== "" ? (
              <div className={`${styles.badge06} ${styles.badge}`}>
                <span>人間ドック（婦人科含む）</span>
              </div>
            ) : (
              ""
            )}
            {facility.toshinkyo_tokutei !== "" ? (
              <div className={`${styles.badge07} ${styles.badge}`}>
                <span>東振協（特定健診）</span>
              </div>
            ) : (
              ""
            )}
          </div>
          <div className={styles.notClicableName}>
            {facility.name}
            {facility.kenporen_o45 !== "" || facility.kenporen_u44 !== "" ? (
              <span className={styles.affiriater}>健保連</span>
            ) : (
              ""
            )}
            {facility.direct !== "" ? (
              <span className={styles.affiriater}>直接</span>
            ) : (
              ""
            )}
            {facility.toshinkyo !== "" ? (
              <span className={styles.affiriater}>東振協</span>
            ) : (
              ""
            )}
            {facility.toshinkyo_tokutei !== "" ? (
              <span className={styles.affiriater}>東振協</span>
            ) : (
              ""
            )}
          </div>
          <BookmarkButton
            facilityId={facility.id}
            handleBookmark={handleBookmark}
            bookmarkedFacilities={bookmarkedFacilities}
          />
          <div className={styles.address}>
            {facility.pref}
            <SafeText text={facility.address} />
            <GoogleMapButton
              facilityPref={facility.pref}
              facilityAddress={facility.address}
              facilityName={facility.name}
            />
            <div className={styles.tel}>
              <span className={styles.icon}>
                <FontAwesomeIcon icon={faPhoneSquare} />
              </span>
              <SafeText text={facility.tel} />
            </div>
          </div>
          {facility.toshinkyo_tokutei !== "" ? (
            ""
          ) : (
            <div className={styles.options}>
              <div className={styles.docTableHeaderWrap}>
                <button
                  className={styles.h3}
                  onClick={() => handleFacilityClick(facility)}
                >
                  {openIds.includes(facility.id) ? (
                    <FontAwesomeIcon icon={faCaretSquareDown} />
                  ) : (
                    <FontAwesomeIcon icon={faTableList} />
                  )}
                  人間ドック受診者負担額
                </button>
              </div>
              <CSSTransition
                in={openIds.includes(facility.id)}
                timeout={200}
                classNames={fade}
                unmountOnExit
              >
                <table>
                  <caption className={styles.tableComment}>※消費税10%の料金</caption>
                  <thead>
                    <tr>
                      <th>種別</th>
                      <th>35歳以上</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th>1日ドック</th>
                      <td>
                        <CurrencyFormatter>
                          {facility.day_o45}
                        </CurrencyFormatter>
                      </td>
                    </tr>
                    <tr>
                      <th>1日ドック（婦人科含む）</th>
                      <td>
                        <CurrencyFormatter>
                          {facility.day_o45_l}
                        </CurrencyFormatter>
                      </td>
                    </tr>
                    <tr>
                      <th>一泊ドック</th>
                      <td>
                        <CurrencyFormatter>
                          {facility.night_o45}
                        </CurrencyFormatter>
                      </td>
                    </tr>
                    <tr>
                      <th>一泊ドック（婦人科含む）</th>
                      <td>
                        <CurrencyFormatter>
                          {facility.night_o45_l}
                        </CurrencyFormatter>
                      </td>
                    </tr>
                    <tr>
                      <th>健保連指定ドック</th>
                      <td>
                        <CurrencyFormatter>
                          {facility.kenporen_o45}
                        </CurrencyFormatter>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CSSTransition>
            </div>
          )}

          {facility.bikou !== "" ? <SafeText text={facility.bikou} /> : ""}
        </div>
      ))}
      {loadedCount < filteredFacilities.length && (
        <div
          ref={lastItemRef}
          style={{ height: 20, background: "transparent" }}
        ></div>
      )}
    </div>
  );
};

export default FacilityList;
